import type { Visit, Worker } from '@/data/types';

interface ScopeUser {
  roles: string[];
  email: string;
  /** The user's worker name, which the office enters as Mitarbeiter. */
  workerName?: string;
}

const same = (a?: string, b?: string) =>
  !!a?.trim() &&
  !!b?.trim() &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** Is this visit assigned to the user — by login or by Mitarbeiter name?
 *  Mirrors the server's scope (apps/api/internal/scope), client #92. */
export function isMyVisit(v: Visit, user: ScopeUser): boolean {
  return same(v.assignedTechId, user.email) || same(v.worker, user.workerName);
}

/** A technician (without org_admin/staff) sees only visits assigned to them.
 *  Admin/staff see everything. Which customers reach a technician's device at
 *  all is decided by the server (client #92). */
export function visibleVisits(
  visits: Visit[],
  user: ScopeUser | null
): Visit[] {
  if (!user) return visits;
  const isAdmin =
    user.roles.includes('org_admin') || user.roles.includes('staff');
  const isTech = user.roles.includes('technician');
  if (isTech && !isAdmin) {
    return visits.filter(v => isMyVisit(v, user));
  }
  return visits;
}

/** The calendar's one technician field (client #92): picking a worker sets
 *  both the Mitarbeiter name and the login it belongs to, so the two can no
 *  longer disagree. A worker without a login clears the login. */
export function assignWorker(v: Visit, w: Worker | undefined): Visit {
  return { ...v, worker: w?.name, assignedTechId: w?.email || undefined };
}

/** The worker a visit is assigned to: by login first, else by name. */
export function visitWorker(v: Visit, workers: Worker[]): Worker | undefined {
  return (
    workers.find(w => same(w.email, v.assignedTechId)) ??
    workers.find(w => same(w.name, v.worker))
  );
}
